import { appData, state } from "../state/state.js";
import {
    markSingleNotificationRead,
    markAllNotificationsRead,
} from "./notificationApi.js";
import {
  renderNotificationShade,
  showSystemToast,
} from "../../shared/ui/notifications.js";
import {
  NOTIFICATION_CLICK_OUTCOMES,
  isApprovalNotification,
  resolveNotificationClickTarget,
} from "./notificationTargets.js";
import {
  openApprovalNotificationTarget,
} from "./approvalNotificationRouter.js";

let closeNotificationShade = () => {};
const MOBILE_NOTIFICATION_MEDIA = "(max-width: 767px)";
const COMPLIANCE_COMPLETED_TARGET_STATUSES = new Set([
  "informational",
  "modal_opened",
  "source_read_only",
]);

/**
 * Registers the notification shade close callback owned by the UI shell.
 * @param {Function} closeShade Callback that closes the notification shade.
 */
export function setNotificationShadeCloser(closeShade) {
  closeNotificationShade = typeof closeShade === "function" ? closeShade : () => {};
}

export { isApprovalNotification };

function closeShadeIfUnpinned() {
  const isMobileLayout = window.matchMedia?.(MOBILE_NOTIFICATION_MEDIA)
    ?.matches === true;

  if (isMobileLayout || !state.viewSettings.notificationsPinned) {
    closeNotificationShade(false);
  }
}

function syncNotificationBellState() {
  document.querySelectorAll(".notification-bell-btn").forEach((bellBtn) => {
    bellBtn.classList.toggle("has-unread", appData.notifications.length > 0);
  });
}

function renderOptimisticNotificationState() {
  syncNotificationBellState();
  if (state.isNotificationShadeOpen) {
    renderNotificationShade();
  }
}

function removeNotificationOptimistically(notification) {
  const index = appData.notifications.findIndex(n => n.id === notification.id);
  if (index === -1) {
    return { index, notification: null };
  }

  const [removedNotification] = appData.notifications.splice(index, 1);
  renderOptimisticNotificationState();

  return {
    index,
    notification: removedNotification,
  };
}

function restoreOptimisticNotification(optimisticState) {
  const notification = optimisticState?.notification;
  if (!notification?.id ||
    appData.notifications.some(n => n.id === notification.id)) {
    return;
  }

  const restoreIndex = optimisticState.index >= 0 ?
    Math.min(optimisticState.index, appData.notifications.length) :
    appData.notifications.length;
  appData.notifications.splice(restoreIndex, 0, notification);
  renderOptimisticNotificationState();
}

/**
 * Handles notification click behavior and navigation.
 * @param {object} notification Notification document data.
 */
export async function handleNotificationClick(notification) {
  const target = resolveNotificationClickTarget(
    notification,
    state.currentUserProfile || {},
  );

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.MODAL_SURFACE &&
    target.surface === "approval") {
    await openApprovalNotification(notification, target);
    return;
  }

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE &&
    target.view === "compliance" && target.notificationId) {
    await openComplianceNotification(notification, target);
    return;
  }

  markSingleNotificationRead(notification.id);

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE) {
    await openBoardNotificationTarget(target, notification);
    return;
  }

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.DETAIL_ROUTE) {
    await openDetailNotificationTarget(target);
    return;
  }

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY) {
    showSystemToast("This notification does not open another view.", {
      id: `notification-info-${notification.id}`,
    });
    return;
  }

  console.error(
    "Notification click failed: Could not determine nav properties",
    notification,
  );
  showSystemToast("This notification no longer has an available destination.", {
    id: `notification-target-${notification.id}`,
  });
}

async function openApprovalNotification(notification, target) {
  closeShadeIfUnpinned();
  await openApprovalNotificationTarget({
    notification,
    target,
    markNotificationRead: markSingleNotificationRead,
  });
}

async function openComplianceNotification(notification, target) {
  let outcome;
  try {
    outcome = await openBoardNotificationTarget(target, notification);
  } catch (error) {
    console.error("Failed to open Compliance notification target:", error);
    showSystemToast("This reminder could not be opened. Please try again.", {
      id: `compliance-notification-failed-${notification.id}`,
    });
    return;
  }
  if (!COMPLIANCE_COMPLETED_TARGET_STATUSES.has(outcome?.status)) {
    showSystemToast("This reminder could not be opened. Please try again.", {
      id: `compliance-notification-failed-${notification.id}`,
    });
    return;
  }
  try {
    await markSingleNotificationRead(notification.id, {throwOnError: true});
  } catch {
    showSystemToast(
        "The reminder could not be marked as read. Please try again.",
        {id: `compliance-notification-read-failed-${notification.id}`},
    );
  }
}

async function openOrderingBoardNotificationTarget(target, navigateWhenConfirmed) {
  const navigated = await navigateWhenConfirmed(target.view, target.subView);
  if (!navigated || state.currentView !== target.view) {
    return {status: "navigation_not_completed"};
  }
  const { openOrderingNotificationItem } =
    await import("../../domains/ordering/orderingModal.js");
  await new Promise((resolve) => window.requestAnimationFrame(resolve));
  if (state.currentView !== target.view) {
    return {status: "navigation_not_completed"};
  }
  return openOrderingNotificationItem(target.itemId || target.contextId);
}

async function openComplianceBoardNotificationTarget(
    target,
    notification,
    navigateWhenConfirmed,
) {
  const {
    getComplianceNotificationTarget,
  } = await import("../../domains/compliance/complianceApi.js");
  const outcome = await getComplianceNotificationTarget(
      target.notificationId || notification.id,
  );
  if (outcome?.outcome !== "authorized") {
    showSystemToast(
        outcome?.message ||
        "This reminder is informational and cannot be opened.",
        {id: `compliance-notification-info-${notification.id}`},
    );
    return {status: "informational"};
  }
  const navigated = await navigateWhenConfirmed("compliance");
  if (!navigated || state.currentView !== "compliance") {
    return {status: "navigation_not_completed"};
  }
  const {
    openComplianceNotificationRecordTarget,
  } = await import("../../domains/compliance/complianceView.js");
  return openComplianceNotificationRecordTarget(outcome);
}

async function openBoardNotificationTarget(target, notification) {
  closeShadeIfUnpinned();

  const { navigate, navigateWhenConfirmed } =
    await import("../routing/navigationController.js");

  if (target.view === "ordering" && (target.itemId || target.contextId)) {
    return await openOrderingBoardNotificationTarget(
        target,
        navigateWhenConfirmed,
    );
  }
  if (target.view === "compliance" && target.notificationId) {
    return await openComplianceBoardNotificationTarget(
        target,
        notification,
        navigateWhenConfirmed,
    );
  }

  navigate(target.view, target.subView);

  if (target.view === "recalls" && target.recallId) {
    const { openRecallNotesHistoryModal } =
      await import("../../domains/recalls/recallsNotesModal.js");
    window.requestAnimationFrame(() => {
      if (state.currentView === "recalls") {
        openRecallNotesHistoryModal(target.recallId);
      }
    });
  }
  return {status: "navigation_started"};
}

async function openDetailNotificationTarget(target) {
  closeShadeIfUnpinned();

  const { restoreDetailRoute } = await import("../routing/detailRouteCoordinator.js");
  await restoreDetailRoute({
    itemId: target.itemId,
    itemType: target.itemType,
  }, {
    source: "notification",
    historyMode: "push",
  });
}

/**
 * Marks a notification read from a single dismiss action.
 * @param {object} notification Notification document data.
 */
export async function handleDismissClick(notification) {
  if (isApprovalNotification(notification.triggerEvent) &&
    notification.parentId) {
    showSystemToast("Approval notifications clear when the request is resolved.", {
      id: `approval-notification-${notification.id}`,
    });
    return;
  }

  const optimisticState = removeNotificationOptimistically(notification);

  try {
    await markSingleNotificationRead(notification.id, { throwOnError: true });
  } catch {
    restoreOptimisticNotification(optimisticState);
    showSystemToast("Notification could not be dismissed. Please try again.", {
      id: `notification-dismiss-failed-${notification.id}`,
    });
  }
}

/**
 * Marks all unread notifications read and manages the triggering button state.
 * @param {Event} [event] Optional click event.
 */
export async function handleDismissAll(event) {
  const btn = event?.currentTarget || document.getElementById("notification-dismiss-all-btn");
  const unreadIds = appData.notifications
    .filter(n => !n.read &&
      !(isApprovalNotification(n.triggerEvent) && n.parentId))
    .map(n => n.id);

  if (unreadIds.length === 0) {return;}

  const originalText = btn?.textContent || "Dismiss All";
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Dismissing...";
    btn.classList.add("opacity-60", "cursor-not-allowed");
  }

  try {
    await markAllNotificationsRead(unreadIds);
  } finally {
    if (btn && document.body.contains(btn)) {
      btn.disabled = false;
      btn.textContent = originalText;
      btn.classList.remove("opacity-60", "cursor-not-allowed");
    }
  }
}
